import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { TrustBadges } from './ui/TrustBadges';
const quizData = [{
  q: '¿Con qué frecuencia sentís ardor o acidez?',
  opts: ['Raramente (1-2 veces por mes)', 'Seguido (1-2 veces por semana)', 'Casi todos los días', 'Varios episodios al día']
}, {
  q: '¿El reflujo te interrumpe el sueño?',
  opts: ['Nunca', 'De vez en cuando', 'Varias veces por semana', 'Casi todas las noches']
}, {
  q: '¿Cuánto tiempo llevás con este problema?',
  opts: ['Menos de 3 meses', 'Entre 3 meses y 1 año', 'Entre 1 y 3 años', 'Más de 3 años']
}, {
  q: '¿Tomás o tomaste omeprazol u otro antiácido?',
  opts: ['Nunca los usé', 'Los uso ocasionalmente', 'Los uso regularmente', 'Los uso todos los días y no me imagino sin ellos']
}, {
  q: '¿Sentís que ciertos alimentos te generan más acidez, pero no sabés cuáles exactamente?',
  opts: ['No, sé perfectamente qué me afecta', 'Tengo una idea vaga', 'Sí, todo parece afectarme', 'Sí, y ya eliminé tantos que casi no como variado']
}, {
  q: '¿El reflujo afecta tu calidad de vida (trabajo, alimentación, vida social)?',
  opts: ['Poco o nada', 'Me molesta pero lo manejo', 'Sí, condiciona mis decisiones diarias', 'Sí, siento que mi vida entera gira en torno a esto']
}];
interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuizModal({
  isOpen,
  onClose
}: QuizModalProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [status, setStatus] = useState<'answering' | 'loading' | 'result'>('answering');
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(0);
      setAnswers([]);
      setStatus('answering');
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleAnswer = (answerIdx: number) => {
    const newAnswers = [...answers, answerIdx];
    setAnswers(newAnswers);
    if (step < quizData.length - 1) {
      setStep(step + 1);
    } else {
      setStatus('loading');
      setTimeout(() => {
        setStatus('result');
      }, 2200);
    }
  };
  const getResult = () => {
    const score = answers.reduce((a, b) => a + b, 0);
    if (score <= 5) {
      return {
        title: 'Reflujo Leve — Pero Vale la Pena Actuar Ahora',
        highlight: 'Tu nivel de reflujo es manejable, pero sin un protocolo claro puede empeorar. El momento ideal para aplicar el Protocolo de Reprogramación Digestiva™ es antes de que el problema escale.',
        desc: 'Tenés la ventaja de actuar a tiempo. Con el protocolo vas a entender exactamente qué hábitos prevenir y cómo mantener tu digestión en orden de forma permanente.'
      };
    } else if (score <= 11) {
      return {
        title: 'Reflujo Moderado — Tu Cuerpo Está Pidiendo un Cambio',
        highlight: 'Tu reflujo ya está afectando tu rutina. Es el momento exacto para aplicar el Protocolo de Reprogramación Digestiva™ antes de que se cronifique por completo.',
        desc: 'La mayoría de las personas en tu situación ven resultados perceptibles en las primeras 72 horas. El protocolo te va a dar claridad sobre qué te afecta específicamente y cómo actuar.'
      };
    } else {
      return {
        title: 'Reflujo Severo — Necesitás un Protocolo Específico',
        highlight: 'Tu reflujo ya controla partes importantes de tu vida. Esto no es normal y no tenés que aceptarlo. El Protocolo de Reprogramación Digestiva™ está diseñado exactamente para situaciones como la tuya.',
        desc: 'Personas con un nivel similar al tuyo son las que más rápido notan el cambio, porque tienen más margen de mejora. El protocolo va directo a la causa, no al síntoma.'
      };
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-40">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-[#0a0f1e]/95 backdrop-blur-md z-[9999] flex items-center justify-center p-5 overflow-y-auto" onClick={onClose} data-id="element-41">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} onClick={e => e.stopPropagation()} className="bg-gradient-to-br from-[#111827] to-navy border border-gold/30 rounded-[16px] max-w-[600px] w-full p-8 md:p-12 relative shadow-[0_0_80px_rgba(200,150,62,0.15)] my-auto" data-id="element-42">
          <button onClick={onClose} className="absolute top-4 right-5 text-gray hover:text-white transition-colors" data-id="element-43">
            <X className="w-6 h-6" data-id="element-44" />
          </button>

          {status === 'answering' && <>
              <div className="flex justify-center gap-2 mb-8" data-id="element-45">
                {quizData.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === step ? 'bg-gold shadow-[0_0_8px_rgba(200,150,62,0.6)] scale-125' : i < step ? 'bg-gold-light' : 'bg-gold/20'}`} data-id="element-46" />)}
              </div>

              <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-47">
                <p className="text-[11px] tracking-[2px] uppercase text-gold mb-2" data-id="element-48">
                  Pregunta {step + 1} de {quizData.length}
                </p>
                <h3 className="font-display text-[22px] font-bold leading-[1.35] text-white mb-7" data-id="element-49">
                  {quizData[step].q}
                </h3>

                <div className="flex flex-col gap-3" data-id="element-50">
                  {quizData[step].opts.map((opt, j) => <button key={j} onClick={() => handleAnswer(j)} className="bg-white/5 border border-white/10 rounded-[8px] p-[14px_18px] text-left text-[15px] text-white flex items-center gap-3 transition-all duration-250 hover:bg-gold/10 hover:border-gold/40 hover:translate-x-1 group" data-id="element-51">
                      <div className="w-[18px] h-[18px] rounded-full border-2 border-gold/40 flex-shrink-0 group-hover:border-gold transition-colors" data-id="element-52" />
                      {opt}
                    </button>)}
                </div>
              </motion.div>
            </>}

          {status === 'loading' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-10" data-id="element-53">
              <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-6" data-id="element-54" />
              <p className="text-[14px] text-gray tracking-[1px]" data-id="element-55">
                Analizando tus respuestas...
              </p>
            </motion.div>}

          {status === 'result' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center" data-id="element-56">
              <div className="inline-block bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/40 text-gold text-[11px] tracking-[2px] uppercase px-4 py-1.5 rounded-full mb-5" data-id="element-57">
                Tu Diagnóstico Digestivo
              </div>
              <h3 className="font-display text-[26px] font-black leading-[1.3] mb-4 text-white" data-id="element-58">
                {getResult().title}
              </h3>
              <div className="bg-gradient-to-br from-gold/10 to-gold/5 border-l-[3px] border-gold p-4 rounded-r-[8px] mb-7 text-left text-[14px] text-white/90 leading-[1.6]" data-id="element-59">
                {getResult().highlight}
              </div>
              <p className="text-[15px] text-gray leading-[1.7] mb-7" data-id="element-60">
                {getResult().desc}
              </p>

              <Button href="#oferta" onClick={onClose} fullWidth data-id="element-61">
                Acceder hoy al Protocolo →
              </Button>
              <TrustBadges className="mt-4" data-id="element-62" />
            </motion.div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}